import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { HomeHeader } from '../home/HomeHeader'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Label } from '../ui/label'
import { ChevronLeft, ChevronRight, Loader2, X, FileText, Image as ImageIcon, Check, AlertCircle } from 'lucide-react'
import { apiClient } from '../../lib/api'
import { BOOK_FILE_FORMATS, IMAGE_FILE_FORMATS, isValidBookFile, isValidImageFile } from '../../lib/constants'
import { LanguageSelector } from '../admin/LanguageSelector'
import { useTranslation } from 'react-i18next'
import { notifyWalletUpdate } from '../../lib/events/wallet-events'
import { notifyTranslationStarted } from '../../lib/events/translation-events'
import { CreditPurchaseDialog } from '../membership/CreditPurchaseDialog'
import type { TranslationCostEstimate, CEFRLevel } from '../../types'

const CEFR_LEVELS: { value: CEFRLevel; label: string; description: string }[] = [
  { value: 'A1', label: 'A1', description: 'Beginner' },
  { value: 'A2', label: 'A2', description: 'Elementary' },
  { value: 'B1', label: 'B1', description: 'Intermediate' },
  { value: 'B2', label: 'B2', description: 'Upper Intermediate' },
  { value: 'C1', label: 'C1', description: 'Advanced' },
]

type WizardStep = 1 | 2 | 3

interface FileMetadata {
  title_original: string
  title_international: string
  author_original: string
  author_international: string
  description: string
  detected_language: string
  estimated_pages: number | null
  cover_image_base64: string | null
}

export function UserBookForm() {
  const { bookId } = useParams<{ bookId: string }>()
  const isEditMode = !!bookId

  // For edit mode, use the old form behavior
  if (isEditMode) {
    return <EditBookForm />
  }

  return <CreateBookWizard />
}

// 3-step wizard for creating new graded books
function CreateBookWizard() {
  const { t } = useTranslation('common')
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const [currentStep, setCurrentStep] = useState<WizardStep>(1)
  const [analyzing, setAnalyzing] = useState(false)
  const [creating, setCreating] = useState(false)

  // Step 1: File upload
  const [originalFile, setOriginalFile] = useState<File | null>(null)
  const [metadata, setMetadata] = useState<FileMetadata | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  
  // Editable metadata (for Step 2)
  const [titleOriginal, setTitleOriginal] = useState('')
  const [titleInternational, setTitleInternational] = useState('')
  const [authorOriginal, setAuthorOriginal] = useState('')
  const [authorInternational, setAuthorInternational] = useState('')
  const [description, setDescription] = useState('')
  const [coverImage, setCoverImage] = useState<File | null>(null)
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null)
  const [extractedCover, setExtractedCover] = useState<string | null>(null)
  const [showMetadataEdit, setShowMetadataEdit] = useState(false)
  
  // Step 2: Language selection
  const [selectedLanguage, setSelectedLanguage] = useState('')
  
  // Step 3: Level selection & cost estimate
  const [selectedLevel, setSelectedLevel] = useState<CEFRLevel | null>(null)
  const [costEstimate, setCostEstimate] = useState<TranslationCostEstimate | null>(null)
  const [estimating, setEstimating] = useState(false)
  const [estimateError, setEstimateError] = useState<string | null>(null)
  const [showCreditDialog, setShowCreditDialog] = useState(false)
  const [estimateRefreshKey, setEstimateRefreshKey] = useState(0)

  // Temporary book ID (created after step 2)
  const [createdBookId, setCreatedBookId] = useState<string | null>(null)

  const handleFileChange = async (file: File) => {
    if (!isValidBookFile(file)) {
      alert(`${t('book.invalidBookFile')} ${BOOK_FILE_FORMATS.displayString}`)
      return
    }

    setOriginalFile(file)
    setAnalyzing(true)

    try {
      const result = await apiClient.analyzeUserBookFile(file)
      
      if (result.success && result.metadata) {
        const meta = result.metadata
        setMetadata(meta)
        
        // Prefill editable fields
        setTitleOriginal(meta.title_original || '')
        setTitleInternational(meta.title_international || '')
        setAuthorOriginal(meta.author_original || '')
        setAuthorInternational(meta.author_international || '')
        setDescription(meta.description || '')
        
        // Set cover
        if (meta.cover_image_base64) {
          const coverDataUrl = `data:image/jpeg;base64,${meta.cover_image_base64}`
          setExtractedCover(coverDataUrl)
          setCoverImagePreview(coverDataUrl)
        }
      }
    } catch (error) {
      console.error('Failed to analyze file:', error)
      alert(t('book.failedToAnalyze'))
    } finally {
      setAnalyzing(false)
    }
  }

  const handleFileDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    
    if (analyzing) return

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      await handleFileChange(files[0])
    }
  }

  const handleRemoveFile = () => {
    setOriginalFile(null)
    setMetadata(null)
    setTitleOriginal('')
    setTitleInternational('')
    setAuthorOriginal('')
    setAuthorInternational('')
    setDescription('')
    setCoverImagePreview(null)
    setExtractedCover(null)
    setCoverImage(null)
  }

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && isValidImageFile(file)) {
      setCoverImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setCoverImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
      setExtractedCover(null)
    }
  }

  // Create book and get cost estimate when entering step 3
  const handleGoToStep3 = async () => {
    if (!originalFile || !selectedLanguage) return

    try {
      setCreating(true)
      
      // Prepare cover file
      let coverToSend = coverImage
      if (!coverImage && extractedCover) {
        const base64Data = extractedCover.split(',')[1]
        const byteString = atob(base64Data)
        const mimeString = extractedCover.split(',')[0].split(':')[1].split(';')[0]
        const ab = new ArrayBuffer(byteString.length)
        const ia = new Uint8Array(ab)
        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i)
        }
        const blob = new Blob([ab], { type: mimeString })
        coverToSend = new File([blob], 'cover.jpg', { type: mimeString })
      }

      // Create the book first
      const result = await apiClient.createUserBook({
        title_original: titleOriginal,
        title_international: titleInternational,
        author_original: authorOriginal,
        author_international: authorInternational,
        original_language: metadata?.detected_language || undefined,
        description: description || undefined,
        estimated_pages: metadata?.estimated_pages || undefined,
        original_file: originalFile,
        cover_image: coverToSend || undefined,
      })

      setCreatedBookId(result.book_id)
      setCurrentStep(3)
      
    } catch (error) {
      console.error('Failed to create book:', error)
      alert(t('book.failedToCreate'))
    } finally {
      setCreating(false)
    }
  }

  // Get cost estimate when level is selected (or after credit purchase)
  useEffect(() => {
    if (currentStep !== 3 || !createdBookId || !selectedLevel || !selectedLanguage) {
      setCostEstimate(null)
      return
    }

    const fetchEstimate = async () => {
      setEstimating(true)
      setEstimateError(null)
      
      try {
        const estimate = await apiClient.translationCost.estimateTranslation({
          bookId: createdBookId,
          targetLanguage: selectedLanguage,
          cefrLevel: selectedLevel
        })
        setCostEstimate(estimate)
      } catch (error: any) {
        console.error('Failed to estimate:', error)
        setEstimateError(error?.message || 'Failed to estimate cost')
      } finally {
        setEstimating(false)
      }
    }

    fetchEstimate()
  }, [currentStep, createdBookId, selectedLevel, selectedLanguage, estimateRefreshKey])

  const handleCreateTranslation = async () => {
    if (!createdBookId || !selectedLanguage || !selectedLevel || !costEstimate) return

    try {
      setCreating(true)
      
      await apiClient.queueUserBookAutoTranslation(
        createdBookId,
        selectedLanguage,
        selectedLevel,
        costEstimate.estimate_id
      )
      
      notifyTranslationStarted()
      notifyWalletUpdate()
      
      // Navigate to the translation (will redirect to first translation)
      navigate(`/user/books/${createdBookId}`)
      
    } catch (error: any) {
      console.error('Failed to queue translation:', error)
      alert(error?.message || t('errors.generic'))
    } finally {
      setCreating(false)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const canGoToStep2 = originalFile && metadata && titleOriginal && titleInternational && authorOriginal && authorInternational
  const canGoToStep3 = canGoToStep2 && selectedLanguage
  const canCreate = canGoToStep3 && selectedLevel && costEstimate && !costEstimate.needs_credit_purchase


  return (
    <div className="create-book-wizard min-h-screen bg-background">
      <HomeHeader user={user} onLogout={handleLogout} />
      
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/my-books')}
            className="back-btn flex items-center text-muted-foreground hover:text-foreground mb-4"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t('book.backToMyBooks')}
          </button>

          <div className="flex items-center gap-3">
            <div className="w-2 h-8 bg-primary rounded-full"></div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {t('book.createGradedBook', 'Create Graded Book')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t('book.createGradedBookDescription', 'Upload a book and translate it to your level')}
              </p>
            </div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="steps-indicator flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center">
              <div className={`step-circle w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                currentStep === step 
                  ? 'bg-primary text-primary-foreground' 
                  : currentStep > step 
                    ? 'bg-primary/20 text-primary' 
                    : 'bg-muted text-muted-foreground'
              }`}>
                {currentStep > step ? <Check className="h-4 w-4" /> : step}
              </div>
              {step < 3 && (
                <div className={`step-line w-16 h-0.5 mx-2 ${
                  currentStep > step ? 'bg-primary' : 'bg-muted'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Upload Book */}
        {currentStep === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>{t('book.step1Title', 'Step 1: Upload Your Book')}</CardTitle>
              <CardDescription>
                {t('book.step1Description', 'Upload an EPUB or FB2 file to get started')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {!originalFile ? (
                <div 
                  className={`file-drop-zone border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                    isDragging 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary'
                  }`}
                  onDrop={handleFileDrop}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
                  onDragLeave={(e) => { e.preventDefault(); setIsDragging(false) }}
                >
                  <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <input
                    id="book-file"
                    type="file"
                    accept={BOOK_FILE_FORMATS.acceptString}
                    onChange={(e) => e.target.files?.[0] && handleFileChange(e.target.files[0])}
                    className="hidden"
                    disabled={analyzing}
                  />
                  <label htmlFor="book-file" className="cursor-pointer">
                    <span className="text-lg text-primary hover:underline font-medium">
                      {isDragging ? t('book.dropFileHere') : t('book.dragDropOrClick')}
                    </span>
                    <p className="text-sm text-muted-foreground mt-2">
                      {t('book.supported')} {BOOK_FILE_FORMATS.displayString}
                    </p>
                  </label>
                  {analyzing && (
                    <div className="mt-6">
                      <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                      <p className="text-sm text-muted-foreground mt-2">
                        {t('book.analyzingFile')}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="file-uploaded space-y-4">
                  <div className="file-info border border-border rounded-lg p-4 bg-muted/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="h-10 w-10 text-primary" />
                        <div>
                          <p className="font-medium text-foreground">{originalFile.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {(originalFile.size / 1024 / 1024).toFixed(2)} MB
                            {metadata?.estimated_pages && (
                              <span className="ml-2">• ~{metadata.estimated_pages} {t('book.pages')}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={handleRemoveFile}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Book Preview */}
                  {metadata && (
                    <div className="book-preview flex gap-4 p-4 border border-border rounded-lg bg-card">
                      {coverImagePreview && (
                        <div className="cover-preview w-20 h-28 flex-shrink-0 rounded overflow-hidden bg-muted">
                          <img src={coverImagePreview} alt="Cover" className="w-full h-full object-cover" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <h3 className="font-semibold text-foreground line-clamp-2">{titleInternational || titleOriginal}</h3>
                        <p className="text-sm text-muted-foreground">{authorInternational || authorOriginal}</p>
                        <button 
                          onClick={() => setShowMetadataEdit(!showMetadataEdit)}
                          className="text-xs text-primary hover:underline mt-2"
                        >
                          {showMetadataEdit ? t('common.hideDetails') : t('common.editDetails')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Editable Metadata (collapsible) */}
                  {showMetadataEdit && (
                    <div className="metadata-edit space-y-4 p-4 border border-border rounded-lg">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="title-original">{t('book.originalTitle')}</Label>
                          <input
                            id="title-original"
                            type="text"
                            value={titleOriginal}
                            onChange={(e) => setTitleOriginal(e.target.value)}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="title-international">{t('book.internationalTitle')}</Label>
                          <input
                            id="title-international"
                            type="text"
                            value={titleInternational}
                            onChange={(e) => setTitleInternational(e.target.value)}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="author-original">{t('book.originalAuthor')}</Label>
                          <input
                            id="author-original"
                            type="text"
                            value={authorOriginal}
                            onChange={(e) => setAuthorOriginal(e.target.value)}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="author-international">{t('book.internationalAuthor')}</Label>
                          <input
                            id="author-international"
                            type="text"
                            value={authorInternational}
                            onChange={(e) => setAuthorInternational(e.target.value)}
                            className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground mt-1"
                          />
                        </div>
                      </div>
                      
                      {/* Cover upload */}
                      <div>
                        <Label>{t('book.coverImage')}</Label>
                        <div className="flex items-center gap-4 mt-1">
                          {coverImagePreview && (
                            <div className="w-16 h-24 rounded overflow-hidden bg-muted">
                              <img src={coverImagePreview} alt="Cover" className="w-full h-full object-cover" />
                            </div>
                          )}
                          <div>
                            <input
                              id="cover-upload"
                              type="file"
                              accept={IMAGE_FILE_FORMATS.acceptString}
                              onChange={handleCoverImageChange}
                              className="hidden"
                            />
                            <label htmlFor="cover-upload">
                              <Button variant="outline" size="sm" asChild>
                                <span><ImageIcon className="h-4 w-4 mr-2" />{t('book.changeCover', 'Change Cover')}</span>
                              </Button>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Next Button */}
              <div className="flex justify-end pt-4">
                <Button 
                  onClick={() => setCurrentStep(2)} 
                  disabled={!canGoToStep2}
                  size="lg"
                >
                  {t('common.next')}
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Choose Language */}
        {currentStep === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>{t('book.step2Title', 'Step 2: Choose Language')}</CardTitle>
              <CardDescription>
                {t('book.step2Description', 'Select the language for your graded book')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Language Selection */}
              <div className="language-selection">
                <LanguageSelector
                  value={selectedLanguage}
                  onChange={setSelectedLanguage}
                />
              </div>

              {/* Book summary */}
              <div className="book-summary flex gap-4 p-4 bg-muted/30 rounded-lg">
                {coverImagePreview && (
                  <div className="w-16 h-24 flex-shrink-0 rounded overflow-hidden bg-muted">
                    <img src={coverImagePreview} alt="Cover" className="w-full h-full object-cover" />
                  </div>
                )}
                <div>
                  <h3 className="font-semibold">{titleInternational}</h3>
                  <p className="text-sm text-muted-foreground">{authorInternational}</p>
                  {metadata?.detected_language && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('book.originalLanguage')}: {metadata.detected_language.toUpperCase()}
                    </p>
                  )}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setCurrentStep(1)}>
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  {t('common.back')}
                </Button>
                <Button 
                  onClick={handleGoToStep3} 
                  disabled={!canGoToStep3 || creating}
                  size="lg"
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('common.processing')}
                    </>
                  ) : (
                    <>
                      {t('common.next')}
                      <ChevronRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Select Level & Confirm */}
        {currentStep === 3 && (
          <Card>
            <CardHeader>
              <CardTitle>{t('book.step3Title', 'Step 3: Select Level')}</CardTitle>
              <CardDescription>
                {t('book.step3Description', 'Choose the CEFR level for your graded book')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Level Selection */}
              <div className="level-selection">
                <div className="grid grid-cols-5 gap-3">
                  {CEFR_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      onClick={() => setSelectedLevel(level.value)}
                      className={`level-option p-4 rounded-lg border text-center transition-colors ${
                        selectedLevel === level.value
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <span className={`text-xl font-bold ${selectedLevel === level.value ? 'text-primary' : ''}`}>
                        {level.label}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1 hidden md:block">{level.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Cost Estimate */}
              {selectedLevel && (
                <div className="cost-estimate border border-border rounded-lg p-4">
                  {estimating ? (
                    <div className="text-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
                      <p className="text-sm text-muted-foreground mt-2">{t('book.estimatingCost', 'Estimating cost...')}</p>
                    </div>
                  ) : estimateError ? (
                    <div className="text-center py-4 text-destructive">
                      <AlertCircle className="h-6 w-6 mx-auto mb-2" />
                      <p className="text-sm">{estimateError}</p>
                    </div>
                  ) : costEstimate ? (
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">{t('book.translationCost', 'Translation cost')}:</span>
                        <span className="font-semibold">${costEstimate.final_cost.toFixed(2)}</span>
                      </div>
                      {costEstimate.discount_percent > 0 && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-green-600">{t('book.membershipDiscount', 'Membership discount')} ({costEstimate.discount_percent}%):</span>
                          <span className="text-green-600">-${costEstimate.discount_amount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">{t('book.yourBalance', 'Your balance')}:</span>
                        <span>${costEstimate.credit_balance.toFixed(2)}</span>
                      </div>
                      {costEstimate.needs_credit_purchase && (
                        <div className="bg-destructive/10 text-destructive p-3 rounded-lg text-sm">
                          <p>{t('book.insufficientCredits', 'Insufficient credits. Please purchase more credits to continue.')}</p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="mt-2"
                            onClick={() => setShowCreditDialog(true)}
                          >
                            {t('book.buyCredits', 'Buy Credits')}
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setCurrentStep(2)
                    setSelectedLevel(null)
                    setCostEstimate(null)
                  }}
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  {t('common.back')}
                </Button>
                <Button 
                  onClick={handleCreateTranslation} 
                  disabled={!canCreate || creating}
                  size="lg"
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('book.creating', 'Creating...')}
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      <span className="hidden sm:inline">{t('book.createTranslation', 'Create Translation')}</span>
                      <span className="sm:hidden">{t('book.create', 'Create')}</span>
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Credit Purchase Dialog */}
      <CreditPurchaseDialog
        open={showCreditDialog}
        onOpenChange={setShowCreditDialog}
        onPurchaseComplete={() => {
          // Refresh the cost estimate after successful purchase
          setEstimateRefreshKey(prev => prev + 1)
        }}
      />
    </div>
  )
}

// Simple edit form for existing books (kept for backward compatibility)
function EditBookForm() {
  const { t } = useTranslation('common')
  const { bookId } = useParams<{ bookId: string }>()
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const [titleOriginal, setTitleOriginal] = useState('')
  const [titleInternational, setTitleInternational] = useState('')
  const [authorOriginal, setAuthorOriginal] = useState('')
  const [authorInternational, setAuthorInternational] = useState('')
  const [description, setDescription] = useState('')
  const [coverImage, setCoverImage] = useState<File | null>(null)
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null)

  useEffect(() => {
    loadBook()
  }, [bookId])

  const loadBook = async () => {
    if (!bookId) return

    try {
      setLoading(true)
      const data = await apiClient.getUserBook(bookId)
      const book = data.book

      setTitleOriginal(book.title_original)
      setTitleInternational(book.title_international)
      setAuthorOriginal(book.author_original)
      setAuthorInternational(book.author_international)
      setDescription(book.description || '')

      if (book.cover_url) {
        setCoverImagePreview(book.cover_url)
      }
    } catch (error) {
      console.error('Failed to load book:', error)
      alert(t('book.loadBookFailed'))
    } finally {
      setLoading(false)
    }
  }

  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setCoverImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setCoverImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!titleOriginal || !titleInternational || !authorOriginal || !authorInternational) {
      alert(t('book.fillRequiredFields'))
      return
    }

    try {
      setSaving(true)
      await apiClient.updateUserBook(bookId!, {
        title_original: titleOriginal,
        title_international: titleInternational,
        author_original: authorOriginal,
        author_international: authorInternational,
        description: description || undefined,
        cover_image: coverImage || undefined,
      })
      alert(t('book.bookUpdated'))
      navigate(`/user/books/${bookId}`)
    } catch (error) {
      console.error('Failed to update book:', error)
      alert(t('book.failedToUpdate'))
    } finally {
      setSaving(false)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <HomeHeader user={user} onLogout={handleLogout} />
        <div className="container mx-auto px-4 py-16 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground mt-4">{t('book.loadingBook')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <HomeHeader user={user} onLogout={handleLogout} />
      
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-muted-foreground hover:text-foreground mb-4"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {t('common.back')}
        </button>

        <Card>
          <CardHeader>
            <CardTitle>{t('book.editBook')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title-original">{t('book.originalTitle')}</Label>
                  <input
                    id="title-original"
                    type="text"
                    value={titleOriginal}
                    onChange={(e) => setTitleOriginal(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="title-international">{t('book.internationalTitle')}</Label>
                  <input
                    id="title-international"
                    type="text"
                    value={titleInternational}
                    onChange={(e) => setTitleInternational(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="author-original">{t('book.originalAuthor')}</Label>
                  <input
                    id="author-original"
                    type="text"
                    value={authorOriginal}
                    onChange={(e) => setAuthorOriginal(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="author-international">{t('book.internationalAuthor')}</Label>
                  <input
                    id="author-international"
                    type="text"
                    value={authorInternational}
                    onChange={(e) => setAuthorInternational(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description">{t('common.description')}</Label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground mt-1 resize-none"
                />
              </div>

              <div>
                <Label>{t('book.coverImage')}</Label>
                <div className="flex items-center gap-4 mt-1">
                  {coverImagePreview && (
                    <div className="w-20 h-28 rounded overflow-hidden bg-muted">
                      <img src={coverImagePreview} alt="Cover" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <input
                    id="cover-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageChange}
                    className="hidden"
                  />
                  <label htmlFor="cover-upload">
                    <Button variant="outline" size="sm" asChild>
                      <span>{t('book.changeCover', 'Change Cover')}</span>
                    </Button>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('book.saving')}
                    </>
                  ) : (
                    t('common.save')
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
